import type { ComponentPropsWithRef, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, useBreakpointValue, useDisclosure } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ShipmentType } from '../../../../../bff-api/baskets/model/ShipmentType';
import { BopisStoreSearchDrawer } from '../../../../../components/bopis-store-search-drawer';
import { Dropdown } from '../../../../../components/dropdown';
import { MAIN_NAV_HEADER_ID } from '../../../../../components/header/Header';
import { ChevronDownBigIcon } from '../../../../../components/icons';
import { SDDStoreSearchDrawer } from '../../../../../components/sdd-store-search-drawer';
import { useIsMobile } from '../../../../../hooks/use-is-mobile';
import { useBopisStore } from '../../../../../hooks/useBopisStore';
import { useSDDAddress } from '../../../../../hooks/useSDDAddress';
import { useSameDayDelivery } from '../../../../../hooks/useSameDayDelivery';
import { useScrollListener } from '../../../../../hooks/useScrollListener';
import { Button, Text } from '../../../../../ui-components';
import { BackToTopButton } from '../../../../../ui-components/back-to-top-button';
import { type UseSearchResult } from '../../../hooks/use-search';
import { useBopisSddFilterEnabled } from '../../../hooks/use-search/useBopisSddFilterEnabled';
import { getSearchSortItems } from '../../../utils/search-page-utils';
import { FilterTags } from '../../filter-tags/FilterTags';
import { AllFiltersSection } from './AllFiltersSection';
import { BopisLocationSelector } from './BopisLocationSelector';
import { FilterDeliveryOptions } from './FilterDeliveryOptions';
import { SddLocationSelector } from './SddLocationSelector';
import { useFilterButtons } from './useFilterButtons';
import type { OrderAddress } from '../../../../../bff-api/baskets/model/OrderAddress';
import type { Store } from '../../../../../bff-api/stores/model';
import type { RevealMode } from '../../filter-tags/AdaptiveRevealList';

interface FilterButtonsProps {
    search: UseSearchResult;
    onOpenFilterDrawer: (index: number) => void;
}

export const FilterButtons: React.FC<FilterButtonsProps> = ({
    search,
    onOpenFilterDrawer,
}: FilterButtonsProps) => {
    const intl = useIntl();
    const sortItems = getSearchSortItems(intl);
    const ref = useRef<HTMLDivElement>(null);
    const [headerRef, setHeaderRef] = useState<HTMLDivElement | null>(null);
    const [isScrolledUnderHeader, setIsScrolledUnderHeader] = useState(false);
    const [showBorder, setShowBorder] = useState(false);
    const [deliveryOption, setDeliveryOption] = useState<ShipmentType | null>(null);

    const { isBopisEnabled, isSddEnabled, isBopisOrSddEnabled } = useBopisSddFilterEnabled();

    const {
        data: bopisStore,
        isFetching: isFetchingBopisStore,
        isLoading: isLoadingBopisStore,
    } = useBopisStore();
    const { data: sddAddress } = useSDDAddress();
    const {
        description: sddDescription,
        sddStore,
        isLoadingSddStore,
        isFetchingSddStore,
    } = useSameDayDelivery();
    const isMobile = useIsMobile();

    const {
        isOpen: isOpenStoreSearch,
        onOpen: onOpenStoreSearch,
        onClose: onCloseStoreSearch,
    } = useDisclosure();
    const {
        isOpen: isOpenLocationSearch,
        onOpen: onOpenLocationSearch,
        onClose: onCloseLocationSearch,
    } = useDisclosure();

    const revealMode =
        useBreakpointValue<RevealMode['mode']>({
            base: 'none',
            md: 'horizontal',
        }) ?? 'none';

    const filterButtons = useFilterButtons(search);

    const topOffset = isMobile ? (headerRef?.offsetHeight ?? 0) : 0;

    useScrollListener(({ scrollY }): void => {
        const headerOffsetHeight = headerRef?.offsetHeight ?? 9999;
        setIsScrolledUnderHeader(scrollY > headerOffsetHeight);

        const distanceFromTheTop = ref?.current?.getBoundingClientRect().top ?? 100;
        const offset = 8 + topOffset;
        setShowBorder(distanceFromTheTop <= offset);
    });

    useEffect(() => {
        const header = document.getElementById(MAIN_NAV_HEADER_ID) as HTMLDivElement;
        setHeaderRef(header);

        if (search.searchParams.bopisStoreId) {
            if (isBopisEnabled) {
                setDeliveryOption(ShipmentType.BOPIS);
            } else {
                search.onChangeParam('bopisStoreId', '');
            }
        } else if (search.searchParams.sddStoreId) {
            if (isSddEnabled) {
                setDeliveryOption(ShipmentType.SAME_DAY_DELIVERY);
            } else {
                search.onChangeParam('sddStoreId', '');
            }
        }

        const handleSearchEvent = (): void => {
            setDeliveryOption(null);
        };

        window.addEventListener('slt-search', handleSearchEvent as EventListener);
        return () => window.removeEventListener('slt-search', handleSearchEvent as EventListener);
    }, []);

    useEffect(() => {
        if (
            !isLoadingBopisStore &&
            deliveryOption === ShipmentType.BOPIS &&
            search.searchParams.bopisStoreId !== bopisStore?.id
        ) {
            search.onBulkChangeFilter(
                [
                    { type: 'bopisStoreId', value: bopisStore?.id ?? '' },
                    { type: 'sddStoreId', value: '' },
                ],
                false,
            );
        }

        if (
            !isLoadingSddStore &&
            deliveryOption === ShipmentType.SAME_DAY_DELIVERY &&
            search.searchParams.sddStoreId !== sddStore?.id
        ) {
            search.onBulkChangeFilter(
                [
                    { type: 'sddStoreId', value: sddStore?.id ?? '' },
                    { type: 'bopisStoreId', value: '' },
                ],
                false,
            );
        }
    }, [isLoadingBopisStore, isLoadingSddStore, deliveryOption]);

    const bopisSddFontSize = isMobile ? 'small' : 'base';

    const bopisStoreComponent = useCallback(
        (chunks: ReactNode[]) => (
            <Text
                id="search-filters-bopis-store-name-label"
                size={bopisSddFontSize}
                style="bold"
                type="link"
                onClick={onOpenStoreSearch}
            >
                {chunks}
            </Text>
        ),
        [onOpenStoreSearch, bopisSddFontSize],
    );

    const sddZipCodeComponent = useCallback(
        (chunks: ReactNode[]) => (
            <Text
                id="search-filters-sdd-zip-code-label"
                size={bopisSddFontSize}
                style="bold"
                type="link"
                onClick={onOpenLocationSearch}
            >
                {chunks}
            </Text>
        ),
        [onOpenLocationSearch, bopisSddFontSize],
    );

    const onChangeBopisFilter = (): void => {
        if (isBopisSelected) {
            setDeliveryOption(null);
            search.onChangeParam('bopisStoreId', '');
        } else {
            setDeliveryOption(ShipmentType.BOPIS);
            search.onBulkChangeFilter([
                { type: 'bopisStoreId', value: bopisStore?.id ?? '' },
                { type: 'sddStoreId', value: '' },
            ]);
        }
    };

    const onChangeSddFilter = (): void => {
        if (isSddSelected) {
            setDeliveryOption(null);
            search.onChangeParam('sddStoreId', '');
        } else {
            setDeliveryOption(ShipmentType.SAME_DAY_DELIVERY);
            search.onBulkChangeFilter([
                { type: 'sddStoreId', value: sddStore?.id ?? '' },
                { type: 'bopisStoreId', value: '' },
            ]);
        }
    };

    const currentSort = search.searchParams.sort
        ? sortItems.find((item) => item.value === search.searchParams.sort)
        : sortItems[0];

    const isBopisSelected = deliveryOption === ShipmentType.BOPIS && isBopisEnabled;
    const isSddSelected = deliveryOption === ShipmentType.SAME_DAY_DELIVERY && isSddEnabled;

    const isBopisStoreEligibleOrNull = !bopisStore?.id || bopisStore?.c_storePickUpEnableFlag;

    const isSddStoreEligibleOrNull =
        !sddAddress?.postalCode || sddStore?.c_isSameDayDeliveryEligible;

    const stickyProps: ComponentPropsWithRef<typeof Flex> = useMemo(
        () => ({
            ref,
            top: topOffset,
            zIndex: 125,
            position: 'sticky',
            borderBottomWidth: showBorder ? 1 : 0,
            borderColor: 'slt-pepper.200',
            backgroundColor: 'white',
        }),
        [ref, topOffset, showBorder],
    );

    const sortByComponent = useMemo(
        () => (
            <Dropdown
                variant={{
                    base: 'slt-dropdown-outline-secondary',
                    md: 'slt-dropdown',
                }}
                id="search-filters-sort-by"
                items={sortItems}
                currentItem={currentSort}
                buttonPrefix={intl.formatMessage({
                    id: 'sort.by',
                    defaultMessage: 'Sort by',
                })}
                onChange={(value: string): void => {
                    if (value !== (search.searchParams.sort ?? '')) {
                        search.onChangeParam('sort', value);
                    }
                }}
            />
        ),
        [sortItems, currentSort, intl, search],
    );

    return (
        <>
            <Flex
                {...(isMobile ? { ...stickyProps } : {})}
                data-testid="search-delivery-buttons"
                alignItems="center"
                paddingY={{ base: 4, md: 0 }} // we need the padding instead gaps when sticky is active
                marginY={{ base: -2, md: 0 }}
                marginX={{ sm: -8, lg: -10, xl: -16 }}
                paddingX={{ base: 4, sm: 8, lg: 10, xl: 16 }}
            >
                <Flex
                    gap={4}
                    shrink={0}
                    grow={1}
                    overflowX="auto"
                    overflowY="hidden"
                    className="scrollbar-hide"
                    justifyContent={{ base: 'space-between', md: 'flex-start' }}
                    alignItems="center"
                    flexWrap="nowrap"
                    width="100%"
                    sx={{
                        overscrollBehaviorX: 'contain',
                        WebkitOverflowScrolling: 'touch',
                        scrollSnapType: 'x proximity',
                        touchAction: 'pan-x',
                    }}
                >
                    {isMobile && (
                        <AllFiltersSection
                            onOpenFilterDrawer={onOpenFilterDrawer}
                            activeFiltersCount={search.activeFilters.length}
                        />
                    )}
                    <FilterDeliveryOptions
                        isBopisSelected={isBopisSelected}
                        isSddSelected={isSddSelected}
                        onChangeBopisFilter={onChangeBopisFilter}
                        onChangeSddFilter={onChangeSddFilter}
                        sddDescription={sddDescription}
                    />
                    {isMobile && !isBopisOrSddEnabled && (
                        <Flex grow={1} justifyContent="flex-start">
                            {sortByComponent}
                        </Flex>
                    )}
                    {!isMobile && (
                        <>
                            <BopisLocationSelector
                                bopisSddFontSize={bopisSddFontSize}
                                bopisStoreName={bopisStore?.name}
                                isBopisSelected={isBopisSelected}
                                isFetchingBopisStore={isFetchingBopisStore}
                                isBopisStoreEligibleOrNull={isBopisStoreEligibleOrNull}
                                bopisStoreComponent={bopisStoreComponent}
                            />
                            <SddLocationSelector
                                bopisSddFontSize={bopisSddFontSize}
                                sddDescription={sddDescription}
                                sddZipCode={sddAddress?.postalCode}
                                isSddSelected={isSddSelected}
                                isFetchingSddStore={isFetchingSddStore}
                                isSddStoreEligibleOrNull={isSddStoreEligibleOrNull}
                                sddZipCodeComponent={sddZipCodeComponent}
                            />
                        </>
                    )}
                </Flex>

                {isMobile && isScrolledUnderHeader && <BackToTopButton />}
            </Flex>
            {isMobile && (isBopisSelected || isSddSelected) && (
                <Flex
                    paddingTop={6} // we need the padding instead gaps when sticky is active
                    marginTop={-6}
                    paddingBottom={2}
                    marginX={{ sm: -8, lg: -10, xl: -16 }}
                    paddingX={{ base: 4, sm: 8, lg: 10, xl: 16 }}
                >
                    <BopisLocationSelector
                        bopisSddFontSize={bopisSddFontSize}
                        bopisStoreName={bopisStore?.name}
                        isBopisSelected={isBopisSelected}
                        isFetchingBopisStore={isFetchingBopisStore}
                        isBopisStoreEligibleOrNull={isBopisStoreEligibleOrNull}
                        bopisStoreComponent={bopisStoreComponent}
                    />
                    <SddLocationSelector
                        bopisSddFontSize={bopisSddFontSize}
                        sddDescription={sddDescription}
                        sddZipCode={sddAddress?.postalCode}
                        isSddSelected={isSddSelected}
                        isFetchingSddStore={isFetchingSddStore}
                        isSddStoreEligibleOrNull={isSddStoreEligibleOrNull}
                        sddZipCodeComponent={sddZipCodeComponent}
                    />
                </Flex>
            )}
            {!isMobile && (
                <Flex
                    {...stickyProps}
                    data-testid="search-filter-buttons"
                    paddingY={{ base: 4, md: 6 }}
                    marginY={{ base: -4, md: -4 }}
                    marginX={{ sm: -8, lg: -10, xl: -16 }}
                    paddingX={{ base: 4, sm: 8, lg: 10, xl: 16 }}
                >
                    <Flex
                        overflowX="auto"
                        overflowY="hidden"
                        gap={2}
                        className="scrollbar-hide"
                        width="100%"
                    >
                        {filterButtons.map((button, index) => (
                            <Button
                                key={button.id}
                                id={`search-filters-button-${button.id}`}
                                variant="menu-chip"
                                onClick={() => onOpenFilterDrawer(index)}
                                flexShrink={0}
                                paddingX={4}
                                paddingY={3}
                                borderColor="slt-pepper.400"
                                textStyle="bold"
                                display="flex"
                                gap={2}
                                rightIcon={<ChevronDownBigIcon />}
                            >
                                {button.label}
                                {button.count > 0 ? ` (${button.count})` : ''}
                            </Button>
                        ))}
                        <AllFiltersSection
                            onOpenFilterDrawer={onOpenFilterDrawer}
                            activeFiltersCount={search.activeFilters.length}
                        />
                        <Flex grow={1} justifyContent="flex-end">
                            {sortByComponent}
                        </Flex>
                    </Flex>
                    {isScrolledUnderHeader && <BackToTopButton />}
                </Flex>
            )}

            {/* filter tags */}
            {search.activeFilters.length > 0 ? (
                <Flex
                    align="center"
                    justify="space-between"
                    paddingTop={6} // we need the padding instead gaps when sticky is active
                    marginTop={-6}
                >
                    <FilterTags
                        search={search}
                        mode={revealMode}
                        className="px-4 sm:px-0"
                        extraItem={
                            <Button
                                id="search-clear-all-filters"
                                variant="tertiary"
                                onClick={search.onClearAllFilters}
                                padding={0}
                                trim
                            >
                                <FormattedMessage
                                    id="search.filters.clear-all"
                                    defaultMessage="Clear All"
                                />
                            </Button>
                        }
                    />
                </Flex>
            ) : null}

            {isBopisSelected && (
                <BopisStoreSearchDrawer
                    id="search-filters-bopis-store-search-drawer"
                    isOpen={isOpenStoreSearch}
                    isLoading={false}
                    onClose={onCloseStoreSearch}
                    onChangeStore={(store: Store): void => {
                        search.onChangeParam('bopisStoreId', store.id);
                    }}
                    closeOnPickUpStore
                />
            )}

            {isSddSelected && (
                <SDDStoreSearchDrawer
                    id="search-filters-sdd-store-search-drawer"
                    isOpen={isOpenLocationSearch}
                    isLoading={false}
                    onClose={onCloseLocationSearch}
                    onChangeAddress={(address: OrderAddress, store: Store | undefined): void => {
                        search.onChangeParam('sddStoreId', store?.id ?? '');
                    }}
                    closeOnSuccess
                />
            )}
        </>
    );
};
